import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LIST_TAB_PARAM, readTabParam, writeTabParam } from '@pmp/utils';

export interface ListTabParamProps {
  activeKey: string | undefined;
  onChange: (key: string) => void;
}

/**
 * Keeps a tabbed page's open tab in the query string, so a link can land on one tab of a route rather
 * than on whichever is first. Spread onto the tabs component in place of `defaultActiveKey`.
 *
 * The open tab is written on mount when the URL does not carry it: the lists on the page decide whether
 * they are on screen from this param alone (`isListOnScreen`), which is what stops the hidden one from
 * reading the URL or reporting itself to the assistant. Written with `replace`, since opening a tab is
 * not a step the back button should have to unwind.
 */
export const useListTabParam = (keys: readonly string[]): ListTabParamProps => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = readTabParam(searchParams, keys);

  useEffect(() => {
    if (activeKey === undefined || searchParams.get(LIST_TAB_PARAM) === activeKey) return;

    setSearchParams(previous => writeTabParam(previous, activeKey), { replace: true });
  }, [activeKey, searchParams, setSearchParams]);

  const onChange = useCallback(
    (key: string) => setSearchParams(previous => writeTabParam(previous, key), { replace: true }),
    [setSearchParams],
  );

  return { activeKey, onChange };
};
